import { Component } from "react";
import { useLocation } from "react-router-dom";
import { allowFailedScreensToRetry } from "../../../app/routing/routePreload";
import styles from "./routeErrorBoundary.module.css";

const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" || /Loading (CSS )?chunk|dynamically imported module/i.test(error?.message || "");

class RouteErrorBoundaryInner extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("Screen failed to render:", error, info);
  }

  componentDidUpdate(previousProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      allowFailedScreensToRetry();
      this.setState({ error: null });
    }
  }

  handleRetry = () => {
    allowFailedScreensToRetry();
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const offline = typeof navigator !== "undefined" && navigator.onLine === false;
    const network = offline || isChunkLoadError(error);
    return (
      <section className={styles.panel} role="alert">
        <h2 className={styles.title}>{network ? "This screen couldn't load" : "Something went wrong on this screen"}</h2>
        <p className={styles.message}>
          {offline
            ? "You appear to be offline. Reconnect, then try again."
            : network
              ? "Check your internet connection and try again."
              : "Try again. If it keeps happening, reload the app."}
        </p>
        <div className={styles.actions}>
          <button type="button" className={styles.primary} onClick={this.handleRetry}>Try again</button>
          <button type="button" className={styles.secondary} onClick={() => window.location.reload()}>Reload app</button>
        </div>
      </section>
    );
  }
}

/** Keeps a failing screen's error inline and clears it when the user navigates elsewhere. */
const RouteErrorBoundary = ({ children }) => {
  const location = useLocation();
  return <RouteErrorBoundaryInner resetKey={location.pathname}>{children}</RouteErrorBoundaryInner>;
};

export default RouteErrorBoundary;
